import React, {useCallback, useMemo, useState} from 'react';
import {components} from 'react-select';
import CreatableSelect from 'react-select/creatable';
import {Button, Grid, IconButton, Tooltip} from '@material-ui/core';
import {FileCopyOutlined, AssignmentOutlined} from '@material-ui/icons';
import {makeStyles, useTheme} from '@material-ui/core/styles/index';
import {Alert} from '@material-ui/lab';
import {
  parseBlocklistDomainsFromInput,
  parseBlocklistDomainsClipboard,
  serializeBlocklistDomainsText,
  urlsFromSelect,
  urlsToOptions,
} from './blocklistHelpers';

const faviconStyle = {
  width: 16,
  height: 16,
  marginRight: 8,
  borderRadius: 2,
  flexShrink: 0,
};

const useStyles = makeStyles(theme => ({
  optionRow: {
    display: 'flex',
    alignItems: 'center',
  },
  optionRowMenu: {
    display: 'flex',
    alignItems: 'center',
    paddingBottom: 7,
  },
  controlAdornment: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    marginLeft: theme.spacing(0.5),
    gap: theme.spacing(0.5),
  },
  copyOutlinedBtn: {
    flexShrink: 0,
    textTransform: 'none',
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 0.15,
    padding: '5px 12px',
    minWidth: 0,
    lineHeight: 1.2,
    marginRight: theme.spacing(0.5),
    boxShadow: 'none',
    '& .MuiButton-startIcon': {
      marginRight: 6,
      marginLeft: -2,
    },
    '& .MuiSvgIcon-root': {
      fontSize: 17,
    },
  },
  pasteIconBtn: {
    padding: 5,
    color: theme.palette.text.secondary,
    '& .MuiSvgIcon-root': {
      fontSize: 19,
    },
    '&:hover:not(.Mui-disabled)': {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.primary.main,
    },
    '&.Mui-disabled': {
      color: theme.palette.action.disabled,
      opacity: 1,
    },
  },
}));

export function CustomUrlList({
  listOfUrls,
  setListofUrls,
  isDisabled,
  canUseClipboard = true,
  onNotify,
}) {
  const classes = useStyles();
  const theme = useTheme();

  const [inputError, setInputError] = useState('');
  const [domainsClipboard, setDomainsClipboard] = useState(null);

  const currentUrls = useMemo(() => urlsFromSelect(listOfUrls), [listOfUrls]);
  const canCopyDomains = canUseClipboard && !isDisabled && currentUrls.length > 0;
  const canPasteDomains = canUseClipboard && !isDisabled;

  const copyDomains = useCallback(() => {
    if (!canCopyDomains) return;

    const urls = [...currentUrls];
    setDomainsClipboard(urls);
    const text = serializeBlocklistDomainsText(urls);
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).catch(() => {});
    }
    onNotify?.(true, 'Blocked advertisers copied. Select another domain, then paste.', '');
  }, [canCopyDomains, currentUrls, onNotify]);

  const pasteDomains = useCallback(async () => {
    if (!canPasteDomains) return;

    let urls = domainsClipboard;
    if (!urls?.length && navigator.clipboard?.readText) {
      try {
        const text = await navigator.clipboard.readText();
        urls = parseBlocklistDomainsClipboard(text);
      } catch {
        urls = null;
      }
    }

    if (!urls?.length) {
      onNotify?.(
        false,
        '',
        'Nothing to paste. Copy blocked advertisers first or paste a comma/space/newline-separated list.'
      );
      return;
    }

    setInputError('');
    setListofUrls(urlsToOptions(urls));
    onNotify?.(true, 'Blocked advertisers pasted. Review changes, then save.', '');
  }, [canPasteDomains, domainsClipboard, onNotify, setListofUrls]);

  const selectStyles = {
    container: provided => ({...provided, minWidth: '100%', marginTop: 0}),
    input: provided => ({...provided, color: 'inherit'}),
    indicatorsContainer: provided => ({
      ...provided,
      flexWrap: 'nowrap',
    }),
    indicatorSeparator: provided => ({
      ...provided,
      backgroundColor: theme.palette.graphColors.themeDarkGray,
    }),
    placeholder: provided => ({...provided, color: 'inherit'}),
    control: provided => ({
      ...provided,
      minHeight: 50,
      minWidth: '100%',
      marginTop: 0,
      background: 'inherit',
      border: `1px solid ${theme.palette.graphColors.themeDarkGray}`,
      // This line disable the blue border
      boxShadow: '0 !important',
      alignItems: 'center',
      '&:hover': {
        //  border: '0 !important',
      },
    }),
    valueContainer: provided => ({
      ...provided,
      padding: '8px 8px',
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 0,
    }),
    multiValue: provided => ({
      ...provided,
      background: theme.palette.background.paper,
      // border: `1px solid ${theme.palette.graphColors.themeDarkGray}`,
      borderRadius: 4,
      margin: 2,
      display: 'flex',
      alignItems: 'center',
      alignSelf: 'center',
    }),
    multiValueLabel: provided => ({
      ...provided,
      color: 'inherit',
      fontWeight: 'bold',
      fontSize: 14,
      display: 'flex',
      alignItems: 'center',
      padding: 0,
      lineHeight: 1,
    }),
    multiValueRemove: provided => ({
      ...provided,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 4,
      margin: 4,
    }),
    menu: provided => ({
      ...provided,
      background: theme.palette.background.default,
      boxShadow: '0 1px 20px rgba(0, 0, 0, .2)',
      zIndex: 9999999,
    }),
    menuList: provided => ({...provided, maxHeight: '60vh'}),
    option: (provided, state) => {
      return {
        ...provided,
        cursor: 'pointer',
        color: 'inherit',
        fontWeight: 'bold',
        fontSize: 14,
        opacity: state.isFocused ? 1 : 0.8,
        backgroundColor: state.isFocused ? 'rgba(199, 199, 199, .05)' : 'none',
        '&:active': {
          background: 'none',
          opacity: 1,
        },
        '&:hover': {
          opacity: 1,
        },
      };
    },
  };

  const formatOptionLabel = ({label}, context) => {
    const faviconUrl = `https://www.google.com/s2/favicons?domain=${encodeURIComponent(label)}`;
    const isChip = context?.context === 'value';
    return (
      <div className={isChip ? classes.optionRow : classes.optionRowMenu}>
        <img src={faviconUrl} alt="" style={faviconStyle} />
        <span style={{lineHeight: 1.4}}>{label}</span>
      </div>
    );
  };

  const addDomainsFromInput = useCallback(
    inputValue => {
      const {valid, invalid} = parseBlocklistDomainsFromInput(inputValue);

      if (!valid.length) {
        setInputError(
          invalid.length
            ? 'Enter valid domains only, e.g. "domain.com". Full URLs are OK — protocol and paths are stripped.'
            : 'Enter a domain only, e.g. "domain.com".'
        );
        return false;
      }

      if (invalid.length) {
        const preview = invalid.slice(0, 3).join(', ');
        const suffix = invalid.length > 3 ? '…' : '';
        setInputError(`Skipped invalid entries: ${preview}${suffix}`);
      } else {
        setInputError('');
      }

      const existing = new Set(urlsFromSelect(listOfUrls).map(url => url.toLowerCase()));
      const newOptions = valid
        .filter(domain => !existing.has(domain.toLowerCase()))
        .map(domain => ({label: domain, value: domain}));

      if (newOptions.length) {
        setListofUrls([...(listOfUrls || []), ...newOptions]);
      }

      return true;
    },
    [listOfUrls, setListofUrls]
  );

  const handleCreateOption = inputValue => {
    addDomainsFromInput(inputValue);
  };

  const handlePaste = useCallback(
    event => {
      const pasted = event.clipboardData?.getData('text') ?? '';
      if (!pasted.trim()) return;

      event.preventDefault();
      addDomainsFromInput(pasted);
    },
    [addDomainsFromInput]
  );

  const Input = useCallback(
    props => <components.Input {...props} onPaste={handlePaste} />,
    [handlePaste]
  );

  const mergedComponents = useMemo(
    () => ({
      Input,
      IndicatorsContainer: props => (
        <components.IndicatorsContainer {...props}>
          <div
            role="presentation"
            className={classes.controlAdornment}
            onMouseDown={e => e.stopPropagation()}
          >
            <Tooltip
              title={
                canCopyDomains
                  ? 'Copy blocked advertisers to use on another domain'
                  : 'Add at least one blocked advertiser first'
              }
            >
              <span>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  disabled={!canCopyDomains}
                  onClick={copyDomains}
                  onMouseDown={e => e.stopPropagation()}
                  startIcon={<FileCopyOutlined />}
                  className={classes.copyOutlinedBtn}
                >
                  Copy blocked advertisers
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Paste blocked advertisers">
              <span>
                <IconButton
                  className={classes.pasteIconBtn}
                  size="small"
                  disabled={!canPasteDomains}
                  onClick={pasteDomains}
                  onMouseDown={e => e.stopPropagation()}
                  aria-label="Paste blocked advertisers"
                >
                  <AssignmentOutlined />
                </IconButton>
              </span>
            </Tooltip>
          </div>
          {props.children}
        </components.IndicatorsContainer>
      ),
    }),
    [
      Input,
      canCopyDomains,
      canPasteDomains,
      copyDomains,
      pasteDomains,
      classes.controlAdornment,
      classes.copyOutlinedBtn,
      classes.pasteIconBtn,
    ]
  );

  return (
    <Grid item xs={12}>
      <CreatableSelect
        isMulti
        isDisabled={isDisabled}
        placeholder="Type or paste domains (comma or space separated, e.g. domain.com)"
        styles={selectStyles}
        components={mergedComponents}
        formatCreateLabel={inputValue => {
          const {valid} = parseBlocklistDomainsFromInput(inputValue);
          if (valid.length > 1) {
            return `Add ${valid.length} domains to the blocked URLs list`;
          }
          return `Add "${inputValue}" to the blocked URLs list`;
        }}
        formatOptionLabel={formatOptionLabel}
        value={listOfUrls}
        onChange={setListofUrls}
        onInputChange={(value, {action}) => {
          if (action === 'input-change' && inputError) {
            setInputError('');
          }
          return value;
        }}
        onCreateOption={handleCreateOption}
        noOptionsMessage={() => 'Type or paste domains to block'}
      />
      {inputError && (
        <Alert
          severity="error"
          style={{
            marginTop: 8,
          }}
        >
          {inputError}
        </Alert>
      )}
    </Grid>
  );
}
